import type { StandardServicePageContent } from "~/content/servicePage";

export const EvacuationFireProtectionInspectionContent: StandardServicePageContent =
  {
    heroTitle: "Przegląd zabezpieczeń ewakuacyjnych i przeciwpożarowych",
    quickFactsTitle: "Najważniejsze informacje w skrócie",
    quickFacts: [
      {
        label: "Jak często",
        value: "Najczęściej co rok",
        description:
          "Urządzenia i elementy wspierające ewakuację oraz ochronę przeciwpożarową powinny być objęte regularnymi przeglądami i konserwacją zgodnie z dokumentacją oraz harmonogramem dla obiektu.",
      },
      {
        label: "Ile kosztuje",
        value: "Najczęściej wycena indywidualna dla obiektu",
        description:
          "Koszt zależy od liczby urządzeń, opraw, drzwi, klap oddymiających i wielkości budynku. Im bardziej rozbudowany układ dróg ewakuacyjnych, tym szerszy zakres testów i dokumentacji.",
      },
      {
        label: "Co obejmuje",
        value: "Oświetlenie ewakuacyjne, drzwi PPOŻ i oddymianie",
        description:
          "Kontrola zwykle obejmuje oprawy awaryjne, oznakowanie dróg ewakuacyjnych, drzwi przeciwpożarowe i podstawowe elementy systemów oddymiania. Po wizycie powinien powstać czytelny protokół z ustaleniami.",
      },
      {
        label: "Kiedy działać wcześniej",
        value: "Po remoncie, awarii lub zmianie układu komunikacji",
        description:
          "Dodatkowy przegląd ma sens po przebudowie korytarzy, zmianie funkcji pomieszczeń, uszkodzeniach drzwi albo problemach z oprawami awaryjnymi. Takie zmiany łatwo wpływają na realne warunki ewakuacji.",
      },
    ],
    faqTitle:
      "Najczęstsze pytania o przegląd zabezpieczeń ewakuacyjnych i przeciwpożarowych",
    sections: [
      {
        title:
          "Na czym polega przegląd zabezpieczeń ewakuacyjnych i przeciwpożarowych",
        paragraphs: [
          `Przegląd zabezpieczeń ewakuacyjnych i przeciwpożarowych polega na sprawdzeniu, czy elementy odpowiedzialne za bezpieczne opuszczenie obiektu i ograniczenie skutków pożaru są sprawne, kompletne i gotowe do działania.`,
          `To usługa ważna dla obiektów użyteczności publicznej, budynków wielorodzinnych, biur, hoteli, hal i lokali usługowych. Dobrze wykonana kontrola nie sprowadza się do formalnego wpisu, ale pokazuje, czy konkretne rozwiązania rzeczywiście spełnią swoją funkcję podczas zagrożenia.`,
          `Po przeglądzie właściciel albo zarządca powinien wiedzieć, które elementy działają prawidłowo, jakie usterki trzeba usunąć i czy układ ewakuacyjny oraz przeciwpożarowy zachowuje spójność w całym budynku.`,
        ],
      },
      {
        title: "Przegląd oświetlenia ewakuacyjnego i drzwi przeciwpożarowych",
        paragraphs: [
          `Kontrola oświetlenia ewakuacyjnego obejmuje sprawdzenie, czy oprawy uruchamiają się po zaniku napięcia, czytelnie oznaczają kierunek ewakuacji i utrzymują wymagany czas działania na zasilaniu awaryjnym. W praktyce liczy się nie tylko to, czy oprawa świeci, ale czy działa tam, gdzie rzeczywiście jest potrzebna.`,
          `Kontrola drzwi przeciwpożarowych obejmuje stan skrzydeł, domykaczy, zawiasów, zamków, uszczelnień i pozostałych elementów wpływających na zachowanie funkcji pożarowej przejścia. Nawet drobne usterki potrafią istotnie osłabić skuteczność zabezpieczenia.`,
          `Najczęstsze problemy to zużyte akumulatory, nieczytelne piktogramy, blokowane drzwi, uszkodzone uszczelki albo zmiany w układzie komunikacji po remontach. Dlatego te elementy wymagają nie tylko obecności w budynku, ale realnej kontroli funkcjonalnej.`,
        ],
      },
      {
        title: "Sprawdzenie oddymiania, dróg ewakuacyjnych i dokumentacji",
        paragraphs: [
          `W wielu obiektach ważnym elementem przeglądu jest także sprawdzenie systemów oddymiania oraz elementów wspierających utrzymanie przejezdnych i czytelnych dróg ewakuacyjnych. Liczy się poprawna reakcja urządzeń na sygnał oraz zgodność całego układu z aktualnym stanem budynku.`,
          `Szczególne znaczenie mają sytuacje po remontach, zmianie funkcji pomieszczeń albo przebudowie korytarzy i klatek schodowych. Wtedy łatwo doprowadzić do rozbieżności między dokumentacją a rzeczywistym układem ewakuacji.`,
          `Po kontroli dokumentacja powinna jasno wskazywać, które zabezpieczenia są sprawne, jakie usterki wymagają naprawy i które elementy trzeba obserwować przy kolejnej kontroli. To podstawa bezpiecznego zarządzania obiektem.`,
        ],
      },
      {
        title: "Cena przeglądu zabezpieczeń ewakuacyjnych i przeciwpożarowych",
        paragraphs: [
          `Cena przeglądu zależy od rodzaju zabezpieczeń, liczby urządzeń, wielkości budynku i zakresu testów przewidzianych do wykonania. Inaczej wycenia się prostą kontrolę kilku opraw i drzwi, a inaczej szerszy przegląd obejmujący kilka systemów w większym obiekcie.`,
          `Na koszt wpływa również potrzeba przygotowania protokołu, wpisów do dokumentacji oraz organizacja testów w działającym budynku. W obiektach komercyjnych często znaczenie ma też harmonogram prac poza standardowymi godzinami funkcjonowania.`,
          `Przy porównywaniu ofert warto sprawdzić, czy cena obejmuje samą wizytę, czy również testy funkcjonalne, ocenę oznakowania i szczegółowe zalecenia po kontroli. To właśnie zakres usługi pozwala realnie porównać propozycje wykonawców.`,
        ],
      },
    ],
    scopeTitle:
      "Zakres usług przeglądu zabezpieczeń ewakuacyjnych i przeciwpożarowych",
    scopeItems: [
      "Kontrola oświetlenia ewakuacyjnego",
      "Test opraw awaryjnych",
      "Weryfikacja oznakowania dróg ewakuacyjnych",
      "Kontrola drzwi przeciwpożarowych",
      "Ocena uszczelnień i domykaczy",
      "Sprawdzenie elementów oddymiania",
      "Weryfikacja dokumentacji",
      "Protokół pokontrolny",
    ],
    seo: {
      title:
        "Przegląd zabezpieczeń ewakuacyjnych i PPOŻ: oświetlenie, drzwi, cena",
      description:
        "Przegląd zabezpieczeń ewakuacyjnych i przeciwpożarowych dla obiektów komercyjnych i wspólnot. Sprawdź zakres kontroli oświetlenia awaryjnego, drzwi PPOŻ i oddymiania.",
    },
  };
